/**
 * EVV Exception Detail Page
 * Sprint 11A: Updated with Layout component and Avida branding
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  AlertTriangle,
  ArrowLeft,
  Clock,
  CheckCircle,
  User,
  Link,
  Unlink,
  History,
  Bell,
  FileCheck,
  Shield,
  Lock,
  Archive,
} from "lucide-react";

const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-800",
  Notified: "bg-yellow-100 text-yellow-800",
  Corrected: "bg-purple-100 text-purple-800",
  Verified: "bg-green-100 text-green-800",
  Closed: "bg-slate-100 text-slate-800",
};

const TYPE_LABELS = {
  ClockInOut: "Clock In/Out",
  GPS: "GPS",
  Verification: "Verification",
  ServiceCode: "Service Code",
  Duration: "Duration",
  Other: "Other",
};

const NOTIFICATION_METHODS = [
  { value: "Email", label: "Email" },
  { value: "Phone", label: "Phone" },
  { value: "InPerson", label: "In Person" },
  { value: "Other", label: "Other" },
];

const EVVExceptionDetailPage = () => {
  const { exceptionId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [exception, setException] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  
  // Status change modals
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showCorrectedModal, setShowCorrectedModal] = useState(false);
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);
  const [showClosedModal, setShowClosedModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  
  // Form states
  const [notifyForm, setNotifyForm] = useState({ notified_via: "", notification_message: "" });
  const [correctedForm, setCorrectedForm] = useState({ correction_note: "" });
  const [verifiedForm, setVerifiedForm] = useState({ verification_note: "" });
  const [closedForm, setClosedForm] = useState({ closure_note: "" });
  const [linkVisitId, setLinkVisitId] = useState("");
  const [archiveReason, setArchiveReason] = useState("");
  
  const canManage = ["Admin", "ComplianceManager", "Scheduler"].includes(user?.role);

  const fetchException = useCallback(async () => {
    setLoading(true);
    try {
      const [excRes, histRes] = await Promise.all([
        api.get(`/evv-exceptions/${exceptionId}`),
        api.get(`/evv-exceptions/${exceptionId}/history`),
      ]);
      setException(excRes.data);
      setHistory(histRes.data.history || []);
      
      // Fetch visits for the client
      if (excRes.data.client_id) {
        const visitsRes = await api.get(`/visits?client_id=${excRes.data.client_id}&page_size=50`);
        setVisits(visitsRes.data.visits || []);
      }
    } catch (error) {
      toast.error("Failed to load exception");
      navigate("/evv-exceptions");
    } finally {
      setLoading(false);
    }
  }, [exceptionId, navigate]);

  useEffect(() => {
    fetchException();
  }, [fetchException]);

  const handleStatusChange = async (newStatus, formData) => {
    try {
      await api.post(`/evv-exceptions/${exceptionId}/status`, {
        new_status: newStatus,
        ...formData,
      });
      toast.success(`Status changed to ${newStatus}`);
      fetchException();
      // Close modals
      setShowNotifyModal(false);
      setShowCorrectedModal(false);
      setShowVerifiedModal(false);
      setShowClosedModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change status");
    }
  };

  const handleLinkVisit = async () => {
    try {
      await api.put(`/evv-exceptions/${exceptionId}/link-visit`, {
        visit_id: linkVisitId || null,
      });
      toast.success(linkVisitId ? "Visit linked" : "Visit unlinked");
      setShowLinkModal(false);
      fetchException();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link visit");
    }
  };

  const handleArchive = async () => {
    try {
      await api.put(`/evv-exceptions/${exceptionId}/archive`, {
        reason: archiveReason,
      });
      toast.success("Exception archived");
      navigate("/evv-exceptions");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive");
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!exception) {
    return null;
  }

  const isArchived = !!exception.archived_at;
  const isClosed = exception.status === "Closed";
  const canEdit = canManage && !isArchived && !isClosed;

  return (
    <Layout title="EVV Exception Details">
      <div>
        {/* Back button and status */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/evv-exceptions")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> EVV Exceptions
            </Button>
            <Badge className={STATUS_COLORS[exception.status]}>{exception.status}</Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Exception Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Exception Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Type</Label>
                    <p className="font-medium">{TYPE_LABELS[exception.exception_type] || exception.exception_type}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Exception Date</Label>
                    <p className="font-medium">{exception.exception_date}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Source</Label>
                    <Badge variant="outline">{exception.source}</Badge>
                  </div>
                  <div>
                    <Label className="text-slate-500">Days Open</Label>
                    <p className={`font-medium ${exception.days_open > 14 ? "text-red-600" : ""}`}>
                      {exception.status === "Closed" ? "-" : `${exception.days_open} days`}
                    </p>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-500">Description</Label>
                  <p className="mt-1 text-slate-700">{exception.description}</p>
                </div>

                <div>
                  <Label className="text-slate-500">Reason</Label>
                  <p className="mt-1 text-slate-700">{exception.reason}</p>
                </div>
              </CardContent>
            </Card>

            {/* Participants Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Participants
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Client</Label>
                    <p className="font-medium">{exception.client_name || exception.client_id}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Caregiver</Label>
                    <p className="font-medium">{exception.caregiver_name || exception.caregiver_id}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-500">Linked Visit</Label>
                  {exception.visit_id ? (
                    <div className="flex items-center gap-2 mt-1">
                      <Link className="h-4 w-4 text-green-600" />
                      <span className="text-green-700">
                        {exception.visit_info?.scheduled_start ? formatDate(exception.visit_info.scheduled_start) : ""} - {exception.visit_info?.service_type || exception.visit_id}
                      </span>
                      {canEdit && (
                        <Button variant="ghost" size="sm" onClick={() => setShowLinkModal(true)}>
                          <Unlink className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-400">Not linked</span>
                      {canEdit && (
                        <Button variant="ghost" size="sm" onClick={() => setShowLinkModal(true)}>
                          <Link className="h-4 w-4" /> Link
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Workflow Progress Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Workflow Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Notification */}
                <div className="border-l-2 border-slate-200 pl-4 pb-4 ml-2">
                  <div className="flex items-center gap-2 mb-1">
                    <Bell className={`h-4 w-4 ${exception.notified_at ? "text-yellow-600" : "text-slate-300"}`} />
                    <span className={exception.notified_at ? "font-medium" : "text-slate-400"}>Notified</span>
                  </div>
                  {exception.notified_at && (
                    <div className="text-sm text-slate-600 ml-6">
                      <p>{formatDateTime(exception.notified_at)} via {exception.notified_via}</p>
                      <p className="italic">&ldquo;{exception.notification_message}&rdquo;</p>
                    </div>
                  )}
                </div>

                {/* Correction */}
                <div className="border-l-2 border-slate-200 pl-4 pb-4 ml-2">
                  <div className="flex items-center gap-2 mb-1">
                    <FileCheck className={`h-4 w-4 ${exception.corrected_at ? "text-purple-600" : "text-slate-300"}`} />
                    <span className={exception.corrected_at ? "font-medium" : "text-slate-400"}>Corrected</span>
                  </div>
                  {exception.corrected_at && (
                    <div className="text-sm text-slate-600 ml-6">
                      <p>{formatDateTime(exception.corrected_at)}</p>
                      <p className="italic">&ldquo;{exception.correction_note}&rdquo;</p>
                    </div>
                  )}
                </div>

                {/* Verification */}
                <div className="border-l-2 border-slate-200 pl-4 pb-4 ml-2">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className={`h-4 w-4 ${exception.verified_at ? "text-green-600" : "text-slate-300"}`} />
                    <span className={exception.verified_at ? "font-medium" : "text-slate-400"}>Verified</span>
                  </div>
                  {exception.verified_at && (
                    <div className="text-sm text-slate-600 ml-6">
                      <p>{formatDateTime(exception.verified_at)} by {exception.verified_by_name}</p>
                      <p className="italic">&ldquo;{exception.verification_note}&rdquo;</p>
                    </div>
                  )}
                </div>

                {/* Closure */}
                <div className="pl-4 ml-2">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className={`h-4 w-4 ${exception.closed_at ? "text-slate-600" : "text-slate-300"}`} />
                    <span className={exception.closed_at ? "font-medium" : "text-slate-400"}>Closed</span>
                  </div>
                  {exception.closed_at && (
                    <div className="text-sm text-slate-600 ml-6">
                      <p>{formatDateTime(exception.closed_at)} by {exception.closed_by_name}</p>
                      <p className="italic">&ldquo;{exception.closure_note}&rdquo;</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* History Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Status History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {history.map((h, idx) => (
                    <div key={h.id || idx} className="flex gap-4 text-sm border-b border-slate-100 pb-2 last:border-0">
                      <div className="text-slate-500 w-40 flex-shrink-0">
                        {formatDateTime(h.changed_at)}
                      </div>
                      <div>
                        <span className="text-slate-400">{h.from_status || "None"}</span>
                        <span className="mx-2">→</span>
                        <Badge className={STATUS_COLORS[h.to_status]}>{h.to_status}</Badge>
                        <span className="text-slate-500 ml-2">by {h.changed_by_email}</span>
                        {h.note && <p className="text-slate-600 mt-1 italic">&ldquo;{h.note}&rdquo;</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Actions Sidebar */}
          <div className="space-y-6">
            {/* Actions Card */}
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isArchived && (
                  <div className="text-center text-slate-500 py-4">
                    <Archive className="h-8 w-8 mx-auto mb-2" />
                    <p>This exception is archived</p>
                    <p className="text-sm">{exception.archived_reason}</p>
                  </div>
                )}

                {!isArchived && canManage && (
                  <>
                    {/* New → Notified */}
                    {exception.status === "New" && (
                      <Dialog open={showNotifyModal} onOpenChange={setShowNotifyModal}>
                        <DialogTrigger asChild>
                          <Button className="w-full bg-yellow-600 hover:bg-yellow-700" data-testid="notify-btn">
                            <Bell className="h-4 w-4 mr-2" /> Notify
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Send Notification</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Notification Method *</Label>
                              <Select
                                value={notifyForm.notified_via}
                                onValueChange={(v) => setNotifyForm({ ...notifyForm, notified_via: v })}
                              >
                                <SelectTrigger data-testid="notify-method-select">
                                  <SelectValue placeholder="Select method" />
                                </SelectTrigger>
                                <SelectContent>
                                  {NOTIFICATION_METHODS.map((m) => (
                                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Notification Message *</Label>
                              <Textarea
                                value={notifyForm.notification_message}
                                onChange={(e) => setNotifyForm({ ...notifyForm, notification_message: e.target.value })}
                                placeholder="Enter notification message..."
                                data-testid="notify-message-input"
                              />
                            </div>
                            <Button
                              onClick={() => handleStatusChange("Notified", notifyForm)}
                              disabled={!notifyForm.notified_via || !notifyForm.notification_message}
                              className="w-full"
                              data-testid="notify-submit-btn"
                            >
                              Send Notification
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}

                    {/* Notified → Corrected */}
                    {exception.status === "Notified" && (
                      <Dialog open={showCorrectedModal} onOpenChange={setShowCorrectedModal}>
                        <DialogTrigger asChild>
                          <Button className="w-full bg-purple-600 hover:bg-purple-700" data-testid="corrected-btn">
                            <FileCheck className="h-4 w-4 mr-2" /> Mark Corrected
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Mark as Corrected</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Correction Note *</Label>
                              <Textarea
                                value={correctedForm.correction_note}
                                onChange={(e) => setCorrectedForm({ correction_note: e.target.value })}
                                placeholder="Describe the correction made..."
                                data-testid="corrected-note-input"
                              />
                            </div>
                            <Button
                              onClick={() => handleStatusChange("Corrected", correctedForm)}
                              disabled={!correctedForm.correction_note}
                              className="w-full"
                              data-testid="corrected-submit-btn"
                            >
                              Mark Corrected
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}

                    {/* Corrected → Verified */}
                    {exception.status === "Corrected" && (
                      <Dialog open={showVerifiedModal} onOpenChange={setShowVerifiedModal}>
                        <DialogTrigger asChild>
                          <Button className="w-full bg-green-600 hover:bg-green-700" data-testid="verify-btn">
                            <Shield className="h-4 w-4 mr-2" /> Verify
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Verify Correction</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Verification Note *</Label>
                              <Textarea
                                value={verifiedForm.verification_note}
                                onChange={(e) => setVerifiedForm({ verification_note: e.target.value })}
                                placeholder="Describe verification process..."
                                data-testid="verify-note-input"
                              />
                            </div>
                            <Button
                              onClick={() => handleStatusChange("Verified", verifiedForm)}
                              disabled={!verifiedForm.verification_note}
                              className="w-full"
                              data-testid="verify-submit-btn"
                            >
                              Verify
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}

                    {/* Verified → Closed */}
                    {exception.status === "Verified" && (
                      <Dialog open={showClosedModal} onOpenChange={setShowClosedModal}>
                        <DialogTrigger asChild>
                          <Button className="w-full" data-testid="close-btn">
                            <Lock className="h-4 w-4 mr-2" /> Close Exception
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Close Exception</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Closure Note *</Label>
                              <Textarea
                                value={closedForm.closure_note}
                                onChange={(e) => setClosedForm({ closure_note: e.target.value })}
                                placeholder="Final closure note..."
                                data-testid="close-note-input"
                              />
                            </div>
                            <Button
                              onClick={() => handleStatusChange("Closed", closedForm)}
                              disabled={!closedForm.closure_note}
                              className="w-full"
                              data-testid="close-submit-btn"
                            >
                              Close Exception
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}

                    {/* Archive */}
                    {!isClosed && (
                      <Dialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="w-full text-red-600 border-red-200" data-testid="archive-btn">
                            <Archive className="h-4 w-4 mr-2" /> Archive
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Archive Exception</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Archive Reason * (min 10 chars)</Label>
                              <Textarea
                                value={archiveReason}
                                onChange={(e) => setArchiveReason(e.target.value)}
                                placeholder="Reason for archiving..."
                                data-testid="archive-reason-input"
                              />
                            </div>
                            <Button
                              onClick={handleArchive}
                              disabled={archiveReason.length < 10}
                              variant="destructive"
                              className="w-full"
                              data-testid="archive-submit-btn"
                            >
                              Archive Exception
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </>
                )}

                {!canManage && !isArchived && (
                  <div className="text-center text-slate-500 py-4">
                    <p>You have read-only access to this exception</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Metadata Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Metadata</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Created</span>
                  <span>{formatDateTime(exception.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Created By</span>
                  <span>{exception.created_by_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Last Updated</span>
                  <span>{formatDateTime(exception.updated_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">ID</span>
                  <span className="text-xs font-mono">{exception.id.substring(0, 8)}...</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Link Visit Dialog */}
        <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{exception?.visit_id ? "Change Linked Visit" : "Link Visit"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Select Visit</Label>
                <Select
                  value={linkVisitId || "unlink"}
                  onValueChange={(v) => setLinkVisitId(v === "unlink" ? "" : v)}
                >
                  <SelectTrigger data-testid="link-visit-select">
                    <SelectValue placeholder="Select a visit or none to unlink" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unlink">None (Unlink)</SelectItem>
                    {visits.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {formatDate(v.scheduled_start)} - {v.service_type} ({v.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleLinkVisit} className="w-full" data-testid="link-visit-submit-btn">
                {linkVisitId ? "Link Visit" : "Unlink Visit"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EVVExceptionDetailPage;
